/**
 * OwnersPanel — beneficiario final / estructura de propiedad (UBO).
 *
 * Lista la cadena de propiedad y control de un expediente (api.listOwners),
 * permite agregar y editar nodos (api.createOwner / api.updateOwner) y la
 * renderiza como un árbol legible usando `parentOwnerId` para anidar. Sigue el
 * sistema de diseño maroon/light-shell (Card, Pill, Button, Dialog, Label) y
 * usa TanStack Query como el resto de las páginas. Cadenas es-PA únicamente.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isUboByOwnership, UBO_THRESHOLD_BY_SECTOR, type EntityMode } from '@cumpli/shared';
import { api } from '../lib/api';
import { useTenantSettings } from '../lib/hooks';
import { effectiveOwnership } from '../lib/ownership';
import { useToast } from '../lib/use-toast';
import type {
  CreateOwnerInput,
  Owner,
  OwnerPatch,
  Persona,
} from '../lib/phase1';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Input,
  Label,
  Pill,
  Skeleton,
  Tooltip,
  cn,
} from './ui';
import { PersonaIcon } from './icons';
import { FileText, Network, Pencil, Plus, ShieldCheck, ShieldAlert, TriangleAlert } from 'lucide-react';

const PERSONA_LABEL: Record<Persona, string> = {
  natural: 'Persona natural',
  legal: 'Persona jurídica',
};

const ownerQueryKey = (expedienteId: string) =>
  ['expedientes', expedienteId, 'owners'] as const;

/** Form state for the create/edit dialog (strings for inputs). */
interface OwnerForm {
  name: string;
  ownerType: Persona;
  ownershipPct: string;
  idNumber: string;
  country: string;
  parentOwnerId: string;
  kycComplete: boolean;
}

const emptyForm = (parentOwnerId: string | null): OwnerForm => ({
  name: '',
  ownerType: 'natural',
  ownershipPct: '',
  idNumber: '',
  country: '',
  parentOwnerId: parentOwnerId ?? '',
  kycComplete: false,
});

const formFromOwner = (o: Owner): OwnerForm => ({
  name: o.name,
  ownerType: o.ownerType,
  ownershipPct: o.ownershipPct != null ? String(o.ownershipPct) : '',
  idNumber: o.idNumber ?? '',
  country: o.country ?? '',
  parentOwnerId: o.parentOwnerId ?? '',
  kycComplete: o.kycComplete,
});

/**
 * Valid "Propietario superior" options for a node: every other owner except the
 * node itself and its own descendants. Excluding descendants prevents creating a
 * cycle (e.g. making A's parent one of A's children), which would make both nodes
 * unreachable from any root and silently disappear from the tree.
 */
function parentOptionsFor(owners: Owner[], editingId: string | null): Owner[] {
  if (!editingId) return owners;
  const childrenByParent = new Map<string, Owner[]>();
  for (const o of owners) {
    if (!o.parentOwnerId) continue;
    const arr = childrenByParent.get(o.parentOwnerId) ?? [];
    arr.push(o);
    childrenByParent.set(o.parentOwnerId, arr);
  }
  const blocked = new Set<string>([editingId]);
  const stack = [editingId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const child of childrenByParent.get(id) ?? []) {
      if (!blocked.has(child.id)) {
        blocked.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return owners.filter((o) => !blocked.has(o.id));
}

export default function OwnersPanel({ expedienteId }: { expedienteId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // Fase 4 drill-down (req#7): abre la vista de la persona con sus documentos exigidos.
  const openDocs = (ownerId: string) =>
    navigate(`/expedientes/${expedienteId}/personas/${ownerId}`);

  const { data: owners, isLoading, isError, refetch } = useQuery({
    queryKey: ownerQueryKey(expedienteId),
    queryFn: () => api.listOwners(expedienteId),
  });

  // El sector del sujeto obligado fija el umbral de beneficiario final (Ley 23);
  // mismo default que el asistente de registro cuando aún no se ha configurado.
  const { data: tenantSettings } = useTenantSettings();
  const sector: EntityMode = tenantSettings?.entityMode ?? 'financiero';

  // Participación EFECTIVA de cada nodo en el sujeto raíz: producto de la cadena
  // de propiedad (100% de una fundación que tiene 24% del sujeto → 24%, no 100%).
  const effective = useMemo(() => effectiveOwnership(owners ?? []), [owners]);

  // dialog: null = closed; { owner: null } = creating; { owner } = editing.
  const [dialog, setDialog] = useState<{ owner: Owner | null; parentId: string | null } | null>(
    null
  );

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ownerQueryKey(expedienteId) });

  const createMutation = useMutation({
    mutationFn: (body: CreateOwnerInput) => api.createOwner(expedienteId, body),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      toast({ message: 'Propietario agregado', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo agregar el propietario', kind: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ ownerId, patch }: { ownerId: string; patch: OwnerPatch }) =>
      api.updateOwner(expedienteId, ownerId, patch),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      toast({ message: 'Propietario actualizado', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo actualizar el propietario', kind: 'error' }),
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // Build the tree: group children by parentOwnerId; roots = parentOwnerId null
  // (or pointing at an owner that isn't in the list, to stay resilient).
  const { roots, childrenOf, ownerCount } = useMemo(() => {
    const list = owners ?? [];
    const byId = new Map(list.map((o) => [o.id, o]));

    // An owner whose ancestor chain loops back to itself (e.g. A→B→A) would never
    // be reachable from any root, so it would silently vanish from the tree.
    // Treat such a node as a root so the data stays visible (and fixable).
    const inCycle = (o: Owner): boolean => {
      const seen = new Set<string>([o.id]);
      let cur = o.parentOwnerId ? byId.get(o.parentOwnerId) : undefined;
      while (cur) {
        if (seen.has(cur.id)) return true;
        seen.add(cur.id);
        cur = cur.parentOwnerId ? byId.get(cur.parentOwnerId) : undefined;
      }
      return false;
    };

    const children = new Map<string, Owner[]>();
    const top: Owner[] = [];
    for (const o of list) {
      const parent =
        o.parentOwnerId && byId.has(o.parentOwnerId) && !inCycle(o) ? o.parentOwnerId : null;
      if (parent) {
        const arr = children.get(parent) ?? [];
        arr.push(o);
        children.set(parent, arr);
      } else {
        top.push(o);
      }
    }
    const byPct = (a: Owner, b: Owner) => (b.ownershipPct ?? 0) - (a.ownershipPct ?? 0);
    top.sort(byPct);
    for (const arr of children.values()) arr.sort(byPct);
    return { roots: top, childrenOf: children, ownerCount: list.length };
  }, [owners]);

  function handleSubmit(form: OwnerForm) {
    const pct = Number(form.ownershipPct);
    const name = form.name.trim();
    if (!name) {
      toast({ message: 'El nombre es requerido', kind: 'error' });
      return;
    }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast({ message: 'El porcentaje debe estar entre 0 y 100', kind: 'error' });
      return;
    }
    const idNumber = form.idNumber.trim();
    const country = form.country.trim();
    const parentOwnerId = form.parentOwnerId || null;

    if (dialog?.owner) {
      // Edit: send a patch. Nullable fields go to null when cleared.
      const patch: OwnerPatch = {
        name,
        ownerType: form.ownerType,
        ownershipPct: pct,
        parentOwnerId,
        idNumber: idNumber || null,
        country: country || null,
        kycComplete: form.kycComplete,
      };
      updateMutation.mutate({ ownerId: dialog.owner.id, patch });
    } else {
      // Create: omit optional string keys when empty (exactOptionalPropertyTypes).
      const body: CreateOwnerInput = {
        subjectRef: expedienteId,
        name,
        ownerType: form.ownerType,
        ownershipPct: pct,
        parentOwnerId,
        kycComplete: form.kycComplete,
        ...(idNumber ? { idNumber } : {}),
        ...(country ? { country } : {}),
      };
      createMutation.mutate(body);
    }
  }

  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl font-medium text-ink">Estructura de propiedad</h2>
          <p className="mt-1 text-sm text-ink-soft">
            <Tooltip
              content={`Persona natural con ≥ ${UBO_THRESHOLD_BY_SECTOR[sector]}% de participación efectiva en el sujeto (Ley 23, umbral del sector).`}
            >
              <span className="underline decoration-dotted">Beneficiarios finales</span>
            </Tooltip>
            {' '}y cadena de control del expediente.
          </p>
        </div>
        <Button onClick={() => setDialog({ owner: null, parentId: null })} disabled={isLoading}>
          <Plus className="h-4 w-4" strokeWidth={2.5} /> Agregar propietario
        </Button>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="grid gap-2.5">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="ml-6 h-14 w-[85%] rounded-xl" />
            <Skeleton className="ml-6 h-14 w-[85%] rounded-xl" />
          </div>
        ) : isError ? (
          <EmptyState
            icon={<TriangleAlert className="h-7 w-7 text-tone-red" strokeWidth={1.75} />}
            title="No se pudo cargar la estructura de propiedad"
            hint="Revisa tu conexión e inténtalo de nuevo."
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Reintentar
              </Button>
            }
          />
        ) : ownerCount === 0 ? (
          <div className="grid place-items-center gap-2 rounded-xl border border-dashed border-line-strong bg-canvas px-6 py-12 text-center">
            <Network className="h-8 w-8 text-ink-faint" strokeWidth={1.5} />
            <div className="font-serif text-lg text-ink">Sin propietarios registrados</div>
            <p className="max-w-sm text-sm text-ink-soft">
              Agrega los beneficiarios finales y la cadena de control para completar la
              debida diligencia.
            </p>
            <Button
              className="mt-2"
              variant="outline"
              onClick={() => setDialog({ owner: null, parentId: null })}
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Agregar el primero
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {roots.map((o) => (
              <OwnerNode
                key={o.id}
                owner={o}
                childrenOf={childrenOf}
                effective={effective}
                sector={sector}
                depth={0}
                onEdit={(owner) => setDialog({ owner, parentId: owner.parentOwnerId })}
                onAddChild={(parentId) => setDialog({ owner: null, parentId })}
                onOpenDocs={openDocs}
              />
            ))}
          </div>
        )}
      </div>

      {dialog && (
        <OwnerDialog
          mode={dialog.owner ? 'edit' : 'create'}
          initial={
            dialog.owner ? formFromOwner(dialog.owner) : emptyForm(dialog.parentId)
          }
          parentOptions={parentOptionsFor(owners ?? [], dialog.owner?.id ?? null)}
          saving={saving}
          onClose={() => (saving ? undefined : setDialog(null))}
          onSubmit={handleSubmit}
        />
      )}
    </Card>
  );
}

/** A single owner row plus its nested children (recursive). */
function OwnerNode({
  owner,
  childrenOf,
  effective,
  sector,
  depth,
  onEdit,
  onAddChild,
  onOpenDocs,
}: {
  owner: Owner;
  childrenOf: Map<string, Owner[]>;
  effective: Map<string, number>;
  sector: EntityMode;
  depth: number;
  onEdit: (o: Owner) => void;
  onAddChild: (parentId: string) => void;
  onOpenDocs: (ownerId: string) => void;
}) {
  const kids = childrenOf.get(owner.id) ?? [];
  // Participación efectiva en el sujeto raíz (cadena multiplicada), no el peso directo.
  const effectivePct = effective.get(owner.id) ?? owner.ownershipPct ?? 0;
  // `nested` = tiene matriz: su % directo es sobre la matriz, no sobre el sujeto.
  const nested = owner.parentOwnerId != null;
  const isUbo = owner.ownerType === 'natural' && isUboByOwnership(effectivePct, sector);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3.5">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-tone-slate-bg text-tone-slate">
          <PersonaIcon persona={owner.ownerType} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => onOpenDocs(owner.id)}
              className="truncate rounded text-left text-sm font-bold text-ink hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              title="Ver documentos de esta persona"
            >
              {owner.name}
            </button>
            {isUbo && (
              <Tooltip
                content={`Beneficiario final (UBO): persona natural con ≥ ${UBO_THRESHOLD_BY_SECTOR[sector]}% de participación efectiva en el sujeto (${formatPct(effectivePct)}).`}
              >
                <Pill tone="green">Beneficiario final</Pill>
              </Tooltip>
            )}
          </div>
          <div className="mt-0.5 truncate font-mono text-[10.5px] text-ink-faint">
            {PERSONA_LABEL[owner.ownerType]}
            {owner.idNumber ? ` · ${owner.idNumber}` : ''}
            {owner.country ? ` · ${owner.country}` : ''}
          </div>
        </div>
        <Tooltip
          content={
            nested
              ? `Participación efectiva en el sujeto. Directo: ${formatPct(owner.ownershipPct)} de su matriz.`
              : 'Participación en el sujeto.'
          }
        >
          <span className="shrink-0 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 font-mono text-[11px] font-semibold text-primary-dark">
            {formatPct(effectivePct)}
          </span>
        </Tooltip>
        <KycChip complete={owner.kycComplete} />
        <button
          onClick={() => onOpenDocs(owner.id)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
          aria-label={`Ver documentos de ${owner.name}`}
          title="Ver documentos"
        >
          <FileText className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <button
          onClick={() => onEdit(owner)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
          aria-label={`Editar ${owner.name}`}
          title="Editar"
        >
          <Pencil className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <button
          onClick={() => onAddChild(owner.id)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
          aria-label={`Agregar propietario bajo ${owner.name}`}
          title="Agregar bajo este nodo"
        >
          <Plus className="h-4 w-4" strokeWidth={2.25} />
        </button>
      </div>

      {kids.length > 0 && (
        <div
          className={cn(
            'ml-5 mt-2.5 grid gap-2.5 border-l-2 border-dashed border-line-strong pl-5',
            depth >= 4 && 'ml-2 pl-3'
          )}
        >
          {kids.map((k) => (
            <OwnerNode
              key={k.id}
              owner={k}
              childrenOf={childrenOf}
              effective={effective}
              sector={sector}
              depth={depth + 1}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onOpenDocs={onOpenDocs}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KycChip({ complete }: { complete: boolean }) {
  return complete ? (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-1 font-mono text-[10px] font-medium text-primary-dark">
      <ShieldCheck className="h-3 w-3" strokeWidth={2} /> KYC
    </span>
  ) : (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-tone-amber-bg px-2 py-1 font-mono text-[10px] font-medium text-tone-amber">
      <ShieldAlert className="h-3 w-3" strokeWidth={2} /> KYC pendiente
    </span>
  );
}

function formatPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  // Avoid trailing ".0" but keep up to two decimals when present.
  const rounded = Math.round(pct * 100) / 100;
  return `${rounded}%`;
}

/** Create / edit dialog. Controlled local form; submit lifts to the parent. */
function OwnerDialog({
  mode,
  initial,
  parentOptions,
  saving,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  initial: OwnerForm;
  parentOptions: Owner[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: OwnerForm) => void;
}) {
  const [form, setForm] = useState<OwnerForm>(initial);
  const set = <K extends keyof OwnerForm>(k: K, v: OwnerForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const selectCls =
    'h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 text-sm text-ink focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <Dialog
      open
      title={mode === 'create' ? 'Agregar propietario' : 'Editar propietario'}
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
      >
        <div>
          <Label htmlFor="owner-name">Nombre completo / razón social</Label>
          <Input
            id="owner-name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Ej. Juan Pérez o Inversiones ABC, S.A."
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="owner-type">Tipo</Label>
            <select
              id="owner-type"
              className={selectCls}
              value={form.ownerType}
              onChange={(e) => set('ownerType', e.target.value as Persona)}
            >
              <option value="natural">Persona natural</option>
              <option value="legal">Persona jurídica</option>
            </select>
          </div>
          <div>
            <Label htmlFor="owner-pct">Participación (%)</Label>
            <Input
              id="owner-pct"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              step="0.01"
              value={form.ownershipPct}
              onChange={(e) => set('ownershipPct', e.target.value)}
              placeholder="0–100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="owner-id">Identificación</Label>
            <Input
              id="owner-id"
              value={form.idNumber}
              onChange={(e) => set('idNumber', e.target.value)}
              placeholder="Cédula / RUC (opcional)"
            />
          </div>
          <div>
            <Label htmlFor="owner-country">País</Label>
            <Input
              id="owner-country"
              value={form.country}
              onChange={(e) => set('country', e.target.value)}
              placeholder="Ej. Panamá (opcional)"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="owner-parent">Propietario superior</Label>
          <select
            id="owner-parent"
            className={selectCls}
            value={form.parentOwnerId}
            onChange={(e) => set('parentOwnerId', e.target.value)}
          >
            <option value="">— Directo (nivel superior) —</option>
            {parentOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-ink-faint">
            Define a quién pertenece esta participación dentro de la cadena.
          </p>
        </div>

        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-line bg-canvas px-3 py-2.5">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={form.kycComplete}
            onChange={(e) => set('kycComplete', e.target.checked)}
          />
          <span className="text-sm font-semibold text-ink">KYC completo</span>
          <span className="text-xs text-ink-faint">Debida diligencia verificada</span>
        </label>

        <DialogActions>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Guardando…' : mode === 'create' ? 'Agregar' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function DialogActions({ children }: { children: ReactNode }) {
  return <div className="flex justify-end gap-3 pt-1">{children}</div>;
}
